/**
 * SubscriptionDetailModal — Admin subscription detail modal (R1 extraction).
 *
 * Extracted from `BillingSubscriptions.tsx` so the `/admin/tenants/:id`
 * Subscriptions tab can reuse the same modal (AC-S5).
 *
 * @see docs/specifications/2026-05-14-tenant-detail-billing-tabs/spec.md (R1)
 */
import React, { useEffect, useRef, useState } from "react";
import {
  useBillingSubscriptionQuery,
  useReDeriveSubscriptionMutation,
  useToggleAutoRenewalMutation,
  useCancelBillingSubscriptionMutation,
} from "../../../../hooks/useAdminProductCatalog";
import { useToast } from "../../../../contexts/ToastContext";
import {
  FEATURE_CODE_LABELS,
  isOverdueScheduled,
  STATUS_BADGE_STYLES,
  STATUS_LABELS,
} from "./subscription-display.constants";
import {
  getBillingIntervalLabel,
  formatEntitlementValue,
} from "./entitlement-formatter";

export interface SubscriptionDetailModalProps {
  subscriptionId: string;
  brandId: string;
  onClose: () => void;
  onMigrate: () => void;
}

export const SubscriptionDetailModal: React.FC<SubscriptionDetailModalProps> = ({
  subscriptionId,
  brandId,
  onClose,
  onMigrate,
}) => {
  const { data: subscription, isLoading } = useBillingSubscriptionQuery(
    subscriptionId,
    brandId
  );
  const reDeriveMutation = useReDeriveSubscriptionMutation(brandId);
  const toggleAutoRenewalMutation = useToggleAutoRenewalMutation(brandId);
  const cancelMutation = useCancelBillingSubscriptionMutation(brandId);
  const { addToast } = useToast();
  const [showConfirmReDerive, setShowConfirmReDerive] = useState(false);
  // AC-14c: cancel form for SCHEDULED subscriptions
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const cancelReasonTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Escape-to-close (closes the cancel form first, then the modal)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showCancelForm) {
          setShowCancelForm(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, showCancelForm]);

  // Auto-focus textarea when cancel form opens
  useEffect(() => {
    if (showCancelForm) {
      cancelReasonTextareaRef.current?.focus();
    }
  }, [showCancelForm]);

  // C2: Focus close button on mount (after subscription loaded).
  useEffect(() => {
    if (subscription) {
      closeButtonRef.current?.focus();
    }
  }, [subscription]);

  // C2: Simple focus trap — cycle Tab/Shift+Tab at boundaries.
  useEffect(() => {
    if (!subscription) return;
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const container = modalRef.current;
      if (!container) return;
      const focusables = container.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [subscription]);

  const handleReDerive = () => {
    reDeriveMutation.mutate(subscriptionId, {
      onSuccess: () => setShowConfirmReDerive(false),
    });
  };

  // AC-14c: submit cancellation of a SCHEDULED subscription
  const handleCancelSubmit = async () => {
    if (cancelReason.length < 10) {
      addToast({
        message: "Powód anulowania musi zawierać co najmniej 10 znaków",
        type: "error",
      });
      return;
    }
    if (cancelReason.length > 1000) {
      addToast({
        message: "Powód anulowania nie może przekraczać 1000 znaków",
        type: "error",
      });
      return;
    }

    try {
      await cancelMutation.mutateAsync({ subscriptionId, reason: cancelReason });
      setShowCancelForm(false);
      setCancelReason("");
    } catch {
      // Error toast already shown by mutation hook (incl. AC-14b 409 instructions)
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (!subscription) return null;

  // AC-8d: SCHEDULED whose period already started but the succession job
  // hasn't switched it to ACTIVE yet — flag it visually for admins.
  const overdue = isOverdueScheduled(subscription);
  const statusBadgeLabel = overdue
    ? "Zaplanowana (opóźniona)"
    : STATUS_LABELS[subscription.status] ?? subscription.status;
  const statusBadgeStyle = overdue
    ? "bg-amber-100 text-amber-800"
    : STATUS_BADGE_STYLES[subscription.status] ?? "bg-blue-100 text-blue-800";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-detail-modal-title"
        className="bg-white rounded-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <h3
              id="subscription-detail-modal-title"
              className="text-lg font-semibold text-neutral-900"
            >
              Subskrypcja — szczegóły
            </h3>
            <div className="flex gap-2 mt-1">
              <span className="text-xs text-neutral-500 font-mono">
                {subscription.id}
              </span>
              {subscription.isLegacy && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                  Legacy — brak product instance
                </span>
              )}
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {!subscription.isLegacy && (
              <button
                onClick={() => setShowConfirmReDerive(true)}
                className="px-3 py-1.5 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50 text-neutral-700 font-medium"
              >
                Odśwież uprawnienia produktu
              </button>
            )}
            {subscription.isLegacy && (
              <button
                onClick={onMigrate}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
              >
                Migruj do nowego systemu
              </button>
            )}
            {/* AC-18: Toggle auto-renewal action button */}
            {!subscription.isLegacy && (
              <button
                onClick={() =>
                  toggleAutoRenewalMutation.mutate({
                    subscriptionId: subscription.id,
                    autoRenewal: subscription.autoRenewal === false,
                  })
                }
                disabled={toggleAutoRenewalMutation.isPending}
                className="px-3 py-1.5 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50 text-neutral-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {toggleAutoRenewalMutation.isPending
                  ? "Zapisywanie..."
                  : subscription.autoRenewal === false
                    ? "Włącz automatyczne odnawianie"
                    : "Wyłącz automatyczne odnawianie"}
              </button>
            )}
            {/* AC-14c: Cancel button — SCHEDULED subscriptions only */}
            {subscription.status === "SCHEDULED" && (
              <button
                type="button"
                onClick={() => setShowCancelForm(true)}
                disabled={showCancelForm}
                className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-50 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anuluj subskrypcję
              </button>
            )}
          </div>

          {/* Cancel SCHEDULED subscription form (AC-14c) */}
          {showCancelForm && (
            <div className="border border-red-200 bg-red-50 rounded-lg p-4 space-y-4">
              <h4 className="text-sm font-semibold text-red-900">
                Anuluj subskrypcję
              </h4>
              <p className="text-xs text-red-700">
                Zaplanowana subskrypcja zostanie trwale anulowana i nie
                aktywuje się na granicy okresu.
              </p>

              <div>
                <label
                  htmlFor="subscription-cancel-reason"
                  className="block text-xs font-medium text-neutral-700 mb-1"
                >
                  Powód anulowania
                </label>
                <textarea
                  ref={cancelReasonTextareaRef}
                  id="subscription-cancel-reason"
                  rows={3}
                  minLength={10}
                  maxLength={1000}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Podaj powód anulowania (min. 10 znaków)"
                  className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                <p
                  className={`text-xs mt-0.5 text-right ${
                    cancelReason.length > 0 && cancelReason.length < 10
                      ? "text-red-500"
                      : "text-neutral-400"
                  }`}
                >
                  {cancelReason.length < 10
                    ? `${cancelReason.length}/10 (minimum)`
                    : `${cancelReason.length}/1000`}
                </p>
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowCancelForm(false);
                    setCancelReason("");
                  }}
                  disabled={cancelMutation.isPending}
                  className="px-4 py-2 text-sm border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                >
                  Cofnij
                </button>
                <button
                  type="button"
                  onClick={handleCancelSubmit}
                  disabled={
                    cancelMutation.isPending ||
                    cancelReason.length < 10 ||
                    cancelReason.length > 1000
                  }
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {cancelMutation.isPending && (
                    <span
                      className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                      aria-hidden="true"
                    />
                  )}
                  Potwierdź anulowanie
                </button>
              </div>
            </div>
          )}

          {/* Re-derive confirm */}
          {showConfirmReDerive && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <p className="text-sm text-amber-800 mb-3">
                Czy na pewno chcesz odświeżyć uprawnienia dla tej subskrypcji?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleReDerive}
                  disabled={reDeriveMutation.isPending}
                  className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50"
                >
                  {reDeriveMutation.isPending ? "Przeliczanie..." : "Potwierdź"}
                </button>
                <button
                  onClick={() => setShowConfirmReDerive(false)}
                  className="px-3 py-1.5 text-xs text-neutral-600 hover:text-neutral-800"
                >
                  Anuluj
                </button>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-4 rounded-md">
            <div>
              <span className="text-xs font-medium text-neutral-500 uppercase">
                Tenant
              </span>
              {subscription.tenantName && (
                <p className="text-sm text-neutral-800 mt-0.5 font-medium">
                  {subscription.tenantName}
                </p>
              )}
              <p className="text-xs font-mono text-neutral-500 mt-0.5">
                {subscription.tenantId}
              </p>
            </div>
            <div>
              <span className="text-xs font-medium text-neutral-500 uppercase">
                Status
              </span>
              <p className="text-sm text-neutral-800 mt-0.5">
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${statusBadgeStyle}`}
                >
                  {statusBadgeLabel}
                </span>
              </p>
            </div>
            <div>
              <span className="text-xs font-medium text-neutral-500 uppercase">
                Produkt
              </span>
              <p className="text-sm text-neutral-800 mt-0.5">
                {subscription.product?.name ?? "—"}
              </p>
            </div>
            <div>
              <span className="text-xs font-medium text-neutral-500 uppercase">
                Interwał rozliczeniowy
              </span>
              <p className="text-sm text-neutral-800 mt-0.5">
                {getBillingIntervalLabel(
                  subscription.status,
                  subscription.billingInterval,
                  subscription.product?.trialDurationDays
                )}
              </p>
            </div>
            {/* AC-17: Auto-renewal field in detail modal info grid */}
            {!subscription.isLegacy && (
              <div>
                <span className="text-xs font-medium text-neutral-500 uppercase">
                  Automatyczne odnawianie
                </span>
                <p className="text-sm text-neutral-800 mt-0.5">
                  {subscription.autoRenewal === false ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                      Wyłączone
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                      Włączone
                    </span>
                  )}
                </p>
              </div>
            )}
            {subscription.currentPeriodStart && (
              <div>
                <span className="text-xs font-medium text-neutral-500 uppercase">
                  Okres
                </span>
                <p className="text-sm text-neutral-800 mt-0.5">
                  {new Date(subscription.currentPeriodStart).toLocaleDateString(
                    "pl-PL"
                  )}{" "}
                  —{" "}
                  {subscription.currentPeriodEnd
                    ? new Date(
                        subscription.currentPeriodEnd
                      ).toLocaleDateString("pl-PL")
                    : "—"}
                </p>
              </div>
            )}
          </div>

          {/* Product Instance Snapshot */}
          {subscription.productInstance && (
            <div>
              <h4 className="text-sm font-semibold text-neutral-900 mb-2">
                Product Instance Snapshot
              </h4>
              <div className="bg-neutral-50 border border-neutral-200 rounded-md p-3">
                <p className="text-sm text-neutral-700">
                  <strong>Nazwa:</strong>{" "}
                  {subscription.productInstance.snapshotName}
                </p>
                <p className="text-sm text-neutral-700">
                  <strong>Typ:</strong>{" "}
                  {subscription.productInstance.snapshotProductType}
                </p>
              </div>
            </div>
          )}

          {/* Feature instances (entitlements) */}
          <div>
            <h4 className="text-sm font-semibold text-neutral-900 mb-2">
              Aktywne cechy subskrypcji ({subscription.entitlements?.length ?? 0})
            </h4>
            {!subscription.entitlements ||
            subscription.entitlements.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-sm text-amber-700">Brak cech produktu</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                        Feature Code
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                        Wartość
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">
                        Aktywny
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200">
                    {subscription.entitlements.map((e) => (
                      <tr key={e.id} className="hover:bg-neutral-50">
                        <td className="px-3 py-2 text-xs">
                          {FEATURE_CODE_LABELS[e.featureCode ?? ""] ||
                            e.featureCode}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {formatEntitlementValue(e.value)}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                              e.isActive
                                ? "bg-green-100 text-green-700"
                                : "bg-neutral-100 text-neutral-500"
                            }`}
                          >
                            {e.isActive ? "Tak" : "Nie"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
